/**
 * The Color — recommendation engine: window.TheColor.Recs
 *
 * ONE engine for three surfaces (docs/edge-2026-09-29/upsell/SPEC.md + BUILD-BRIEF.md):
 *   • PDP "Pairs well with"        (sections/product-main.liquid)  list 'pdp_pairs'
 *   • cart drawer upsell           (snippets/cart-drawer.liquid)   list 'cart_upsell'
 *   • /cart page upsell            (same renderer as the drawer)   list 'cart_upsell'
 *
 * A function-by-function port of scripts/data/pairings/reference_engine_curated.py
 * (SPEC v1 + amendments R1–R7). Names follow the Python so QC can diff behaviour:
 *   parse_size→parseSize, default_variant→defaultVariant, size_match→sizeMatch,
 *   natural_variant→naturalVariant, same_job→sameJob, slot_key→slotKey,
 *   fallback_list→fallbackList, expand→expandCart, cart_recs→forCart,
 *   find_swap→findSwap, pdp_recs→forProduct, reason→reason.
 *
 * Data:  the curated graph (assets/tc-pairs-graph.json = graph.curated.min.json),
 *        loaded once per page view (memory → sessionStorage → fetch).
 * Money: ALWAYS live pence from /products/<h>.js and /cart.js, never the graph.
 * Ranking (cart): S1 routine → S2 free-delivery closer (least overshoot, only
 *        under the threshold) → S3 litre pump → S4 honest swap → fill. Max 3,
 *        deterministic (every fetch resolves before sorting by (key, handle)).
 * Honesty: a swap only when OUR duo/twin is ≥ 50p cheaper than OUR singles at the
 *        same size, computed live, and never when it would lose free delivery.
 *        "Adds free delivery" only while the selected variant genuinely crosses.
 *
 * House rules: ES6-safe ES5 inside an IIFE, no frameworks; every storage access
 * in try/catch; callers escape everything that reaches innerHTML (helpers below).
 */
(function () {
  'use strict';

  var W = window;
  var TheColor = (W.TheColor = W.TheColor || {});
  if (TheColor.Recs) return;

  /* ------------------------------------------------------------ constants -- */
  var PUMP = 'paul-mitchell-litre-bottle-pump';
  var TRAVEL_MAX = 75;            // ml / g: at or below = travel size
  var MIN_SWAP_SAVING = 50;       // pence; below this no swap card
  var CART_PRICE_FLOOR_CAP = 4000;
  var PDP_PRICE_FLOOR_CAP = 4500;
  var MAX_CARDS = 3;
  var UPSIZE_MAX_RATIO = 2.0;     // R4
  var CAP_SAME_RANGE_POS = 3;     // R3
  var S2_POOL_POS = 4;            // S2 pool: partners + first 4 list items of each seed
  var WAVE_SIZE = 8;              // ≤ 8 new /products/<h>.js requests per wave
  var MAX_WAVES = 2;
  var DISMISS_KEY = 'tc-upsell-dismissed';   // kept from the old drawer so existing dismissals survive
  var GRAPH_KEY = 'tc-recs-graph';
  var WASH = { shampoo: 1, conditioner: 1, wash2in1: 1 };
  var CONFLICT = [['shampoo', 'wash2in1'], ['conditioner', 'wash2in1']];
  var ROLE_UNIQUE = { brush: 1, tool: 1, accessory: 1 };
  var FALLBACK = {   // SPEC 3.5: target roles, same range first, else the house list
    shampoo: ['conditioner', 'treatment', 'leavein'], conditioner: ['shampoo', 'treatment', 'leavein'],
    wash2in1: ['styler', 'treatment'], treatment: ['shampoo', 'conditioner', 'leavein'],
    leavein: ['shampoo', 'conditioner', 'heat'], scalp: ['shampoo', 'conditioner'],
    styler: ['heat', 'finish', 'brush'], finish: ['heat', 'serum', 'brush'],
    heat: ['serum', 'brush', 'finish'], serum: ['heat', 'shampoo', 'brush'],
    refresh: ['styler', 'finish'], tool: ['heat', 'brush', 'serum'], brush: ['heat', 'serum', 'finish'],
    body: ['shampoo'], accessory: []
  };
  var HOUSE_BY_ROLE = { heat: 'heat', serum: 'serum', finish: 'finish', brush: 'brush-styling' };
  var ROLE_STEP = { scalp: 0, shampoo: 1, wash2in1: 1, conditioner: 2, treatment: 3, leavein: 4,
    heat: 4, styler: 5, serum: 6, finish: 6, refresh: 6, tool: 7, brush: 7, accessory: 8, body: 9 };
  /* Degraded mode (graph unavailable): partner by exception map, else the
     -shampoo/-conditioner handle swap. Built from the catalogue 2026-08-24. */
  var PARTNER = {
    'pm-shampoo-one': 'pm-the-conditioner', 'pm-shampoo-two': 'pm-the-conditioner',
    'pm-shampoo-three': 'pm-the-conditioner', 'pm-the-conditioner': 'pm-shampoo-one',
    'pm-awapuhi-shampoo-original': 'pm-awapuhi-conditioner', 'pm-awapuhi-conditioner': 'pm-awapuhi-shampoo-original',
    'pm-awapuhi-wild-ginger-nourishing-shampoo': 'pm-awapuhi-wild-ginger-cream-rinse',
    'pm-awapuhi-wild-ginger-cream-rinse': 'pm-awapuhi-wild-ginger-nourishing-shampoo',
    'pm-tea-tree-hemp-restoring-shampoo-body-wash': 'pm-tea-tree-hemp-restoring-conditioner-body-lotion',
    'pm-tea-tree-hemp-restoring-conditioner-body-lotion': 'pm-tea-tree-hemp-restoring-shampoo-body-wash',
    'pm-tea-tree-special-shampoo-bar': 'pm-tea-tree-special-conditioner',
    'pm-platinum-plus-shampoo': 'pm-platinum-blonde-conditioner',
    'pm-pet-calming-lavender-mint-shampoo': 'pm-pet-soothing-oatmeal-conditioner',
    'pm-pet-nourishing-awapuhi-shampoo': 'pm-pet-soothing-oatmeal-conditioner',
    'pm-pet-super-bright-shampoo': 'pm-pet-soothing-oatmeal-conditioner',
    'pm-pet-tear-free-shampoo': 'pm-pet-soothing-oatmeal-conditioner',
    'pm-pet-soothing-oatmeal-conditioner': 'pm-pet-nourishing-awapuhi-shampoo'
  };
  var REASON_BY_JOB = {
    'toning': 'Tones between washes', 'rinse': 'Clarifying rinse step', 'colour-deposit': 'Colour top-up',
    'colour-lock': 'Locks in colour', 'heat-prime': 'Preps before heat', 'oil': 'Finishing oil',
    'scalp-scrub': 'Scalp scrub step', 'wipes': 'Quick clean between baths',
    'pet-spray': 'Coat spray to finish', 'shine': 'Adds shine', 'dry-shampoo': 'Refresh between washes',
    'dry-conditioner': 'Refresh between washes', 'gloss': 'Gloss treatment step',
    'treatment-system': 'Salon treatment step', 'waterless-wash': 'No-rinse wash between baths',
    'strength-spray': 'Daily strengthening step', 'curl-finish': 'Smooths & shines curls'
  };
  var REASON_BY_ROLE = {
    heat: 'Protect before heat', serum: 'Seal in smoothness & shine', finish: 'Hold the finish',
    brush: 'The brush for this style', treatment: 'Weekly treatment step', leavein: 'Leave-in step',
    scalp: 'Scalp care step', styler: 'Styling step', conditioner: 'Completes your routine',
    shampoo: 'Completes your routine'
  };

  /* -------------------------------------------------------------- helpers -- */
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  var escapeAttr = escapeHtml;

  function money(pence) {
    var n = Math.round(Number(pence) || 0);
    return '£' + (n / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function norm(t) {
    return String(t == null ? '' : t).replace(/\s+/g, '').toLowerCase();
  }

  /* "75 ml" / "300ml" / "85 g" / "Gallon (3.78L)" (read inside brackets) → {n, u}. */
  function parseSize(t) {
    var s = String(t == null ? '' : t);
    var m = /\(([^)]+)\)/.exec(s);
    if (m) s = m[1];
    m = /^\s*(\d+(?:\.\d+)?)\s*(ml|g|l)\s*$/i.exec(s);
    if (!m) return null;
    var n = parseFloat(m[1]);
    var u = m[2].toLowerCase();
    if (u === 'l') { n *= 1000; u = 'ml'; }
    return { n: n, u: u };
  }

  function isLitre(size) {
    var s = parseSize(size);
    return !!(s && s.u === 'ml' && s.n === 1000);
  }

  function cmpKey(a, b) {
    for (var i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return a[i] < b[i] ? -1 : 1;
    }
    return 0;
  }
  function cmpStr(a, b) { return a < b ? -1 : (a > b ? 1 : 0); }

  function ssGet(k) { try { return W.sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { W.sessionStorage.setItem(k, v); } catch (e) { /* private mode / quota */ } }

  var warned = {};
  function warnOnce(msg) {
    if (warned[msg]) return;
    warned[msg] = true;
    try { if (W.console && W.console.warn) W.console.warn('[TheColor.Recs] ' + msg); } catch (e) {}
  }

  function rootUrl() {
    return (W.Shopify && W.Shopify.routes && W.Shopify.routes.root) || '/';
  }

  /* Shopify CDN URL at a given width (.js gives protocol-relative URLs). */
  function sized(url, width) {
    if (!url) return '';
    var u = String(url).replace(/_\d+x(\d+)?(?=\.[a-z]+(\?|$))/i, '').replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function threshold() {
    var t = W.theme && Number(W.theme.freeShippingThreshold);
    if (t > 0) return t;
    return (graph && graph.threshold) || 3000;
  }

  /* ------------------------------------------------------------- graph ---- */
  var graph = null;
  var graphPromise = null;
  var derived = {};         // nodes derived at runtime for products missing from the graph (SPEC 2.4)
  var fallbackIndex = null; // "g|r|s" → handles sorted by (step, handle)

  function validGraph(g) {
    return !!(g && g.p && g.b && typeof g.p === 'object');
  }

  function useGraph(g) {
    graph = g;
    fallbackIndex = null;
    return g;
  }

  function ready() {
    if (graphPromise) return graphPromise;
    var url = W.theme && W.theme.pairsGraphUrl;
    if (!url) {
      warnOnce('no pairsGraphUrl — degraded mode');
      graphPromise = Promise.resolve(null);
      return graphPromise;
    }
    var cached = ssGet(GRAPH_KEY);
    if (cached) {
      try {
        var c = JSON.parse(cached);
        if (c && c.url === url && validGraph(c.g)) {
          graphPromise = Promise.resolve(useGraph(c.g));
          return graphPromise;
        }
      } catch (e) { /* corrupt entry — refetch */ }
    }
    graphPromise = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('graph ' + r.status); return r.text(); })
      .then(function (txt) {
        var g = JSON.parse(txt);
        if (!validGraph(g)) throw new Error('graph shape');
        /* One entry only (keyed by the versioned asset URL inside), so a new
           deploy replaces rather than accumulates 85 KB copies. */
        ssSet(GRAPH_KEY, '{"url":' + JSON.stringify(url) + ',"g":' + txt + '}');
        return useGraph(g);
      })
      .catch(function (err) {
        warnOnce('graph unavailable (' + (err && err.message) + ') — degraded mode');
        return null;
      });
    return graphPromise;
  }

  function node(h) {
    return (graph && graph.p[h]) || derived[h] || null;
  }

  function isBundle(h) {
    return !!(graph && graph.b[h]);
  }

  function pinned(seedH, c) {
    return (graph && graph.pin && graph.pin[seedH + '>' + c]) || null;
  }

  function bundleComponents(h, variantTitle) {
    var b = graph.b[h];
    if (!b) return null;
    if (b[variantTitle]) return b[variantTitle];
    var keys = Object.keys(b);
    for (var i = 0; i < keys.length; i++) {
      if (norm(keys[i]) === norm(variantTitle)) return b[keys[i]];
    }
    return keys.length ? b[keys[0]] : [];
  }

  /* SPEC 2.4 deriveRole(): a product on sale before the graph is rebuilt.
     Same table as build_pairings.py role_of()/silo_of(); the range is guessed
     from the longest shared handle prefix with a graph node in the same silo
     (else 'unknown', so the fallback uses house lists only). */
  function roleOf(h, t) {
    t = String(t || '');
    if (t === 'Accessories' || h.indexOf('bottle-pump') > -1) return ['accessory', 'pump'];
    if (t === 'Grooming Wipes') return ['accessory', 'wipes'];
    if (t === 'Brush') return ['brush', h.indexOf('round') > -1 ? 'brush-round' : (h.indexOf('paddle') > -1 ? 'brush-paddle' : 'brush-styling')];
    if (t === 'Hair Tools') return ['tool', h.indexOf('dryer') > -1 ? 'tool-dryer' : 'tool-iron'];
    if (h.indexOf('dry-shampoo') > -1) return ['refresh', 'dry-shampoo'];
    if (h.indexOf('dry-conditioner') > -1) return ['refresh', 'dry-conditioner'];
    if (t === 'Co-Wash' || t === 'Shampoo & Conditioner') return ['wash2in1', 'wash2in1'];
    if (t === 'Body Care' || h.indexOf('shave-gel') > -1) return ['body', h.indexOf('shave') > -1 ? 'shave' : (h.indexOf('soap') > -1 ? 'soap' : 'body-bar')];
    if (t === 'Shampoo') return ['shampoo', 'shampoo'];
    if (t === 'Conditioner') return ['conditioner', 'conditioner'];
    if (t === 'Scalp Scrub' || t === 'Scalp Treatment' || t === 'Scalp Treatment Kit' || h.indexOf('scalp-drops') > -1 || (t === 'Serum' && h.indexOf('scalp') > -1)) {
      return ['scalp', t === 'Scalp Scrub' ? 'scalp-scrub' : (t.indexOf('Kit') > -1 ? 'scalp-kit' : 'scalp-leave-on')];
    }
    if (t === 'Heat Protectant' || h.indexOf('heat-styling-spray') > -1) return ['heat', h.indexOf('neuro-prime') > -1 ? 'heat-prime' : 'heat-protect'];
    if (t === 'Leave-In Treatment') return ['leavein', 'leavein'];
    if (t === 'Treatment' || t === 'Hair Mask') {
      if (h.indexOf('gloss-drops') > -1) return ['serum', 'serum-shine'];
      if (h.indexOf('styling-treatment-oil') > -1) return ['serum', 'oil'];
      if (h.indexOf('volumizing-liquid') > -1) return ['styler', 'styler-volume'];
      if (h.indexOf('toning-drops') > -1) return ['treatment', 'toning'];
      if (h.indexOf('color-depositing') > -1) return ['treatment', 'colour-deposit'];
      if (h.indexOf('rinse') > -1) return ['treatment', 'rinse'];
      return ['treatment', 'treatment'];
    }
    if (t === 'Serum' || t === 'Hair Oil') {
      if (h.indexOf('round-trip') > -1 || h.indexOf('twirl-around') > -1) return ['styler', 'styler-curl'];
      return ['serum', t === 'Hair Oil' ? 'oil' : 'serum-smooth'];
    }
    if (t === 'Hairspray') return ['finish', 'hairspray'];
    if (t === 'Finishing Spray') return ['finish', 'colour-lock'];
    if (t === 'Styling Spray') {
      if (h.indexOf('pm-pet-') === 0) return ['leavein', 'pet-spray'];
      if (h.indexOf('taming-spray') > -1) return ['leavein', 'detangle'];
      if (h.indexOf('shine-spray') > -1) return ['finish', 'shine'];
      if (h.indexOf('hemp-multitasking') > -1) return ['leavein', 'leavein'];
      if (h.indexOf('blow-out') > -1) return ['styler', 'styler-blowout'];
      if (h.indexOf('sea-spray') > -1 || h.indexOf('texturizing-spray') > -1) return ['styler', 'styler-texture'];
      return ['styler', 'styler-volume'];
    }
    if (t === 'Mousse') return ['styler', h.indexOf('root-lift') > -1 ? 'styler-volume' : 'styler-foam'];
    if (t === 'Styling Gel' || t === 'Styling Glaze' || t === 'Styling Lotion') return ['styler', 'styler-gel'];
    if (t === 'Styling Cream') return ['styler', 'styler-cream'];
    if (t === 'Styling Paste' || t === 'Pomade' || t === 'Styling Clay') return ['styler', 'styler-paste'];
    if (t === 'Styling Wax') return ['styler', h.indexOf('spray-wax') > -1 ? 'styler-texture' : 'styler-paste'];
    return ['other', 'other'];
  }

  function deriveRole(h, type) {
    if (!graph || !h || graph.p[h] || derived[h]) return node(h);
    var rj = roleOf(h, type);
    var silo = h.indexOf('pm-pet-') === 0 ? 'pet' : (h.indexOf('pm-mitch-') === 0 ? 'mitch' : 'core');
    var range = 'unknown';
    var tokens = h.split('-');
    for (var k = tokens.length - 1; k >= 3 && range === 'unknown'; k--) {
      var prefix = tokens.slice(0, k).join('-') + '-';
      var counts = {};
      Object.keys(graph.p).forEach(function (o) {
        var m = graph.p[o];
        if (o.indexOf(prefix) === 0 && m.s === silo && !isBundle(o)) counts[m.g] = (counts[m.g] || 0) + 1;
      });
      var best = null;
      Object.keys(counts).sort().forEach(function (g) { if (!best || counts[g] > counts[best]) best = g; });
      if (best) range = best;
    }
    derived[h] = { r: rj[0], j: rj[1], s: silo, g: range, x: [], pt: null, derived: true };
    return derived[h];
  }

  /* ---------------------------------------------------------- products ---- */
  var productCache = {};   // handle → Promise<ProductJs|null> (null results evicted)

  function getProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return productCache[handle];
    var p = fetch(rootUrl() + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var v = d && d.id ? d : null;
        if (!v) delete productCache[handle];
        return v;
      })
      .catch(function () { delete productCache[handle]; return null; });
    productCache[handle] = p;
    return p;
  }

  /* Seed the cache with products the page already has (PDP SSR), in .js shape. */
  function prime(products) {
    (products || []).forEach(function (p) {
      if (p && p.handle && p.id && p.variants && !productCache[p.handle]) {
        productCache[p.handle] = Promise.resolve(p);
      }
    });
  }

  function isCached(h) { return !!productCache[h]; }

  /* Fetch the next wave in priority order. Cached handles are free; at most
     WAVE_SIZE NEW requests go out, all in parallel. Resolves to the number of
     new requests made. */
  function fetchWave(order, have) {
    var batch = [];
    var fresh = 0;
    var seen = {};
    for (var i = 0; i < order.length; i++) {
      var h = order[i];
      if (!h || seen[h] || Object.prototype.hasOwnProperty.call(have, h)) continue;
      seen[h] = true;
      if (!isCached(h)) {
        if (fresh >= WAVE_SIZE) continue;
        fresh++;
      }
      batch.push(h);
    }
    return Promise.all(batch.map(function (h) {
      return getProduct(h).then(function (p) { have[h] = p; });
    })).then(function () { return fresh; });
  }

  /* -------------------------------------------------------- variants ------ */
  /* Sellable = .js `available` (DENY singles), and for CONTINUE-policy bundles
     also the build-time stock_ok flag graph.k (a missing flag fails safe). */
  function sellable(p) {
    if (!p || !p.variants) return [];
    var k = graph && graph.k && graph.k[p.handle];
    return p.variants.filter(function (v) {
      if (!v || v.available !== true) return false;
      if (!k) return true;
      if (k[v.title] === true) return true;
      if (k[v.title] === false) return false;
      var keys = Object.keys(k);
      for (var i = 0; i < keys.length; i++) {
        if (norm(keys[i]) === norm(v.title)) return k[keys[i]] === true;
      }
      return false;
    });
  }

  function defaultVariant(p) {
    var vs = sellable(p);
    if (!vs.length) return null;
    var sizedVs = [];
    vs.forEach(function (v) { var s = parseSize(v.title); if (s) sizedVs.push([s, v]); });
    if (sizedVs.length) {
      var big = sizedVs.filter(function (sv) { return sv[0].n > TRAVEL_MAX; });
      var pool = big.length ? big : sizedVs;
      var best = pool[0];
      pool.forEach(function (sv) { if (sv[0].n < best[0].n) best = sv; });
      return best[1];
    }
    return vs[0];
  }

  function sizeMatch(p, want) {
    var vs = sellable(p);
    if (!vs.length) return null;
    for (var i = 0; i < vs.length; i++) {
      if (norm(vs[i].title) === norm(want)) return vs[i];
    }
    var ws = parseSize(want);
    if (ws) {
      var best = null, bestKey = null;
      vs.forEach(function (v) {
        var s = parseSize(v.title);
        if (s && s.u === ws.u) {
          var key = [Math.abs(s.n - ws.n), s.n];
          if (!best || cmpKey(key, bestKey) < 0) { best = v; bestKey = key; }
        }
      });
      if (best) return best;
    }
    return defaultVariant(p);
  }

  function findTitle(vs, title) {
    for (var i = 0; i < vs.length; i++) {
      if (norm(vs[i].title) === norm(title)) return vs[i];
    }
    return null;
  }

  /* SPEC 4 natural variant + R2 (pins, default-variant override). */
  function naturalVariant(c, seedH, seedSize, p) {
    var pin = pinned(seedH, c);
    if (pin) return findTitle(sellable(p), pin);   // pinned: that variant or nothing (card dropped)
    var rc = node(c) && node(c).r;
    var rs = node(seedH) && node(seedH).r;
    if (WASH[rc] && WASH[rs] && seedSize) return sizeMatch(p, seedSize);
    var dv = graph && graph.dv && graph.dv[c];
    if (dv) {
      var hit = findTitle(sellable(p), dv);
      if (hit) return hit;
    }
    return defaultVariant(p);
  }

  function sameJob(a, b) {
    var na = node(a), nb = node(b);
    if (!na || !nb) return false;
    var ja = na.j, jb = nb.j;
    if (ja === jb) return true;
    return CONFLICT.some(function (s) { return s.indexOf(ja) > -1 && s.indexOf(jb) > -1; });
  }

  function slotKey(h) {
    var n = node(h);
    if (!n) return 'handle:' + h;
    return ROLE_UNIQUE[n.r] ? 'role:' + n.r : 'job:' + n.j;
  }

  function fallbackList(seedH) {
    var n = node(seedH);
    if (!n) return [];
    if (!fallbackIndex) {
      fallbackIndex = {};
      Object.keys(graph.p).forEach(function (h) {
        var m = graph.p[h];
        var k = m.g + '|' + m.r + '|' + m.s;
        (fallbackIndex[k] = fallbackIndex[k] || []).push(h);
      });
      Object.keys(fallbackIndex).forEach(function (k) {
        fallbackIndex[k].sort(function (a, b) {
          var sa = ROLE_STEP[graph.p[a].r], sb = ROLE_STEP[graph.p[b].r];
          sa = sa == null ? 9 : sa; sb = sb == null ? 9 : sb;
          return (sa - sb) || cmpStr(a, b);
        });
      });
    }
    var out = [];
    (FALLBACK[n.r] || []).forEach(function (role) {
      var same = fallbackIndex[n.g + '|' + role + '|' + n.s] || [];
      var pick = same.slice(0, 1);
      if (!pick.length && HOUSE_BY_ROLE[role] && n.s === 'core') {
        pick = ((graph.house && graph.house[HOUSE_BY_ROLE[role]]) || []).filter(function (h) { return graph.p[h]; }).slice(0, 1);
      }
      out = out.concat(pick);
    });
    return out;
  }

  /* -------------------------------------------------------------- cart ---- */
  /* /cart.js → engine lines. The same variant on several lines (properties)
     counts as one handle with quantities summed (SPEC 12.8). */
  function cartLines(cart) {
    var out = [], byVid = {};
    ((cart && cart.items) || []).forEach(function (it) {
      if (!it || !it.handle) return;
      var ex = byVid[it.variant_id];
      if (ex) { ex.qty += it.quantity; ex.keys.push(it.key); return; }
      var vt = it.variant_title || (it.variant_options && it.variant_options.length === 1 && it.variant_options[0]) || 'Default Title';
      var l = {
        handle: it.handle, variant: vt, qty: it.quantity || 1, keys: [it.key],
        variantId: it.variant_id, productId: it.product_id,
        price: it.original_price != null ? it.original_price : it.price,
        productType: it.product_type || '', title: it.product_title || ''
      };
      byVid[it.variant_id] = l;
      out.push(l);
    });
    return out;
  }

  /* expand(): bundle lines (and the R5 kit) become their components. Owned =
     every cart handle + every component. £0 lines are owned, never seeds. */
  function expandCart(lines) {
    var seeds = [], owned = {};
    lines.forEach(function (l, i) {
      owned[l.handle] = true;
      if (isBundle(l.handle)) {
        (bundleComponents(l.handle, l.variant) || []).forEach(function (cp) {
          owned[cp[0]] = true;
          seeds.push({ handle: cp[0], size: cp[1], qty: cp[2] * l.qty, line: i, via: l.handle });
        });
      } else {
        if (!node(l.handle)) deriveRole(l.handle, l.productType);
        if (l.price === 0) return;
        seeds.push({ handle: l.handle, size: l.variant, qty: l.qty, line: i, via: null });
      }
    });
    return { seeds: seeds, owned: owned };
  }

  function litreCount(seeds) {
    return seeds.reduce(function (n, s) { return n + (isLitre(s.size) ? s.qty : 0); }, 0);
  }

  function dismissedSets(list) {
    var handles = {}, ids = {};
    (list || []).forEach(function (d) {
      d = String(d);
      if (d.indexOf('h:') === 0) handles[d.slice(2)] = true;
      else ids[d] = true;
    });
    return { handles: handles, ids: ids };
  }

  function appliedCodes(cart) {
    return ((cart && cart.discount_codes) || []).filter(function (c) { return c && c.applicable; })
      .map(function (c) { return String(c.code || '').toUpperCase(); });
  }

  /* SPEC 6.4 gap maths — the same shelf-money formula cart.js uses for the bar. */
  function gapMaths(cart, opts) {
    var T = threshold();
    var total = cart.total_price || 0;
    var codes = appliedCodes(cart);
    var rate = (codes.length && cart.original_total_price > 0) ? (cart.total_discount || 0) / cart.original_total_price : 0;
    if (!(rate >= 0 && rate < 1)) rate = 0;
    var freeCodes = ((opts && opts.freeShippingCodes) || (W.theme && W.theme.freeShippingCodes) || [])
      .map(function (c) { return String(c).toUpperCase(); });
    var freeByCode = codes.some(function (c) { return freeCodes.indexOf(c) > -1; });
    var gap = freeByCode ? 0 : Math.max(0, T - total);
    var gapShelf = gap ? Math.ceil(gap / (1 - rate)) : 0;
    return { T: T, total: total, rate: rate, gap: gap, gapShelf: gapShelf, freeByCode: freeByCode };
  }

  /* ------------------------------------------------------------ labels ---- */
  function reason(c, seedH, kind) {
    if (kind === 'pump') return 'Pump for your litre';
    if (kind === 'swap') return 'Same bottles, lower price';
    var nc = node(c);
    if (!nc) return 'Completes your routine';
    var ns = seedH ? node(seedH) : null;
    if (ns && ns.pt === c) return 'Matching ' + ({ conditioner: 'conditioner', shampoo: 'shampoo' }[nc.r] || 'partner');
    if (REASON_BY_JOB[nc.j]) return REASON_BY_JOB[nc.j];
    if (nc.r === 'body') return 'Same Tea Tree range';
    return REASON_BY_ROLE[nc.r] || 'Completes your routine';
  }

  function relation(c, seedH, kind) {
    if (kind === 'pump' || kind === 'swap' || kind === 'gap') return kind;
    var ns = seedH ? node(seedH) : null;
    return ns && ns.pt === c ? 'partner' : 'routine';
  }

  /* ------------------------------------------------------------- cards ---- */
  function sizeLabel(title) {
    return (!title || title === 'Default Title') ? '' : String(title);
  }

  function isTravel(p, v) {
    var s = parseSize(v.title);
    if (!s || s.n > TRAVEL_MAX) return false;
    return (p.variants || []).some(function (o) { var so = parseSize(o && o.title); return !!(so && so.n > TRAVEL_MAX); });
  }

  function unitPrice(v) {
    var s = parseSize(v.title);
    if (!s || !s.n) return '';
    return money(Math.round(v.price * 100 / s.n)) + '/100 ' + s.u;
  }

  function variantImage(p, v) {
    var raw = '';
    if (v && v.featured_image) raw = typeof v.featured_image === 'string' ? v.featured_image : (v.featured_image.src || '');
    if (!raw && p) raw = typeof p.featured_image === 'string' ? p.featured_image : ((p.featured_image && p.featured_image.src) || (p.image && p.image.src) || '');
    return raw ? sized(raw, 160) : '';
  }

  function optionFor(p, v, gm) {
    var chips = [];
    var crosses = !!(gm && gm.gap && v.price >= gm.gapShelf);
    if (crosses) chips.push('Adds free delivery');
    var travel = isTravel(p, v);
    if (travel) chips.push('Travel size');
    return {
      variantId: v.id, variantTitle: v.title, sizeLabel: sizeLabel(v.title), price: v.price,
      crossesGap: crosses, travel: travel, chips: chips, unitPrice: unitPrice(v), image: variantImage(p, v)
    };
  }

  function makeCard(p, h, kind, d, a, seedH, gm, extra) {
    var def = optionFor(p, d, gm);
    var alt = a ? optionFor(p, a, gm) : null;
    var n = node(h);
    var card = {
      kind: kind, rel: relation(h, seedH, kind), handle: h, productId: p.id,
      title: p.title || '', url: p.url || (rootUrl() + 'products/' + h),
      image: def.image, variantId: def.variantId, variantTitle: def.variantTitle, sizeLabel: def.sizeLabel,
      price: def.price, reason: reason(h, seedH, kind), why: '', chips: def.chips,
      unitPrice: alt ? def.unitPrice : '', crossesGap: def.crossesGap,
      alt: alt ? { variantId: alt.variantId, variantTitle: alt.variantTitle, price: alt.price, crossesGap: alt.crossesGap } : null,
      options: alt ? [def, alt] : [def],
      role: n ? n.r : '', slot: slotKey(h), seed: seedH || null
    };
    if (extra) Object.keys(extra).forEach(function (k) { card[k] = extra[k]; });
    return card;
  }

  /* ----------------------------------------------------------- forCart ---- */
  function forCart(cart, opts) {
    opts = opts || {};
    return ready().then(function (g) {
      if (!cart || !cart.items || !cart.items.length) return [];
      return g ? cartRecs(cart, opts) : degradedCart(cart, opts);
    });
  }

  function cartRecs(cart, opts) {
    var lines = cartLines(cart);
    var ex = expandCart(lines);
    var seeds = ex.seeds, owned = ex.owned;
    var gm = gapMaths(cart, opts);
    var gap = gm.gap, gapShelf = gm.gapShelf;
    var cap = Math.max(CART_PRICE_FLOOR_CAP, gm.total);
    var dis = dismissedSets(opts.dismissed);
    var maxCards = opts.maxCards || MAX_CARDS;
    var ownedSingles = seeds.map(function (s) { return s.handle; }).filter(function (h) { return !!node(h); });

    function staticOk(c, s) {
      var nc = node(c), ns = node(s.handle);
      if (!nc || nc.derived || owned[c] || dis.handles[c] || c === PUMP || isBundle(c)) return false;
      if (nc.s !== ns.s) return false;
      if (ownedSingles.some(function (o) { return node(o).s === nc.s && sameJob(o, c); })) return false;
      return true;
    }

    var cands = {};
    seeds.forEach(function (s, si) {
      var ns = node(s.handle);
      if (!ns || (ns.r === 'accessory' && ns.j === 'pump')) return;
      var lst = (ns.x || []).slice();
      if (lst.filter(function (c) { return staticOk(c, s); }).length < 2) {
        fallbackList(s.handle).forEach(function (c) { if (lst.indexOf(c) < 0) lst.push(c); });
      }
      lst.forEach(function (c, pos) {
        if (!staticOk(c, s)) return;
        var tier = ns.pt === c ? 1 : (pos < (ns.x || []).length ? 2 : 3);
        var key = [tier, pos, si];
        if (!cands[c] || cmpKey(key, cands[c].key) < 0) cands[c] = { key: key, seed: s, pos: pos, tier: tier };
      });
    });

    var ranked = Object.keys(cands).sort(function (a, b) { return cmpKey(cands[a].key, cands[b].key) || cmpStr(a, b); });
    var litre = litreCount(seeds);
    var wantPump = litre > 0 && !owned[PUMP];
    var swapPlan = planSwaps(lines);
    var specials = (wantPump ? [PUMP] : []).concat(swapPlan.handles);
    var have = {};
    var waves = 0;

    function evaluate() {
      function prod(h) {
        if (!Object.prototype.hasOwnProperty.call(have, h)) return null;
        var p = have[h];
        if (p && dis.ids[String(p.id)]) return null;   // legacy product-id dismissals
        return p;
      }

      function pickVariants(c) {
        var info = cands[c];
        var p = prod(c);
        if (!p) return null;
        var nat = naturalVariant(c, info.seed.handle, info.seed.size, p);
        if (!nat) return null;
        if (pinned(info.seed.handle, c)) {
          return (info.tier === 1 || nat.price <= cap) ? { d: nat, a: null, p: p } : null;   // R2: no alternative
        }
        var exempt = info.tier === 1 || (info.tier === 2 && info.pos < CAP_SAME_RANGE_POS &&
          node(c).g === node(info.seed.handle).g);
        if (!exempt && nat.price > cap) {
          var ok = sellable(p).filter(function (v) { return v.price <= cap; });
          if (!ok.length) return null;
          nat = ok[0];
          ok.forEach(function (v) { if (v.price > nat.price) nat = v; });
        }
        var d = nat, a = null;
        if (gap) {   // rule G1 + R4
          var crossing = sellable(p).filter(function (v) { return v.price >= gapShelf && (exempt || v.price <= cap); });
          var closer = null;
          crossing.forEach(function (v) { if (!closer || v.price < closer.price) closer = v; });
          if (closer && norm(closer.title) !== norm(nat.title)) {
            if (nat.price >= gapShelf) { d = nat; a = closer; }
            else if (closer.price <= UPSIZE_MAX_RATIO * nat.price) { d = closer; a = nat; }
            else { d = nat; a = closer; }
          }
        }
        return { d: d, a: a, p: p };
      }

      var chosen = [], keys = {}, prods = {};
      function canAdd(c) { return !prods[c] && !keys[slotKey(c)]; }
      function push(c, kind, pv) {
        chosen.push(makeCard(pv.p, c, kind, pv.d, pv.a, cands[c] ? cands[c].seed.handle : null, gm));
        prods[c] = true;
        keys[slotKey(c)] = true;
      }
      function pumpCard(p, v) {
        var card = makeCard(p, PUMP, 'pump', v, null, null, gm);
        card.reason = 'Pump for your litre' + (litre >= 2 ? 's' : '');
        card.slot = 'role:accessory';
        return card;
      }

      /* S1 — best routine completer */
      var i, c, pv, s1Missing = false;
      for (i = 0; i < ranked.length; i++) {
        c = ranked[i];
        if (!Object.prototype.hasOwnProperty.call(have, c)) { s1Missing = true; break; }   // never pick past an unfetched better candidate
        pv = pickVariants(c);
        if (pv && canAdd(c)) { push(c, 'routine', pv); break; }
      }

      /* S2 — free-delivery closer, least overshoot (only under the threshold) */
      var s2Missing = false;
      if (gap) {
        var opts2 = [];
        Object.keys(cands).forEach(function (h) {
          var info = cands[h];
          if (!(info.tier === 1 || info.pos < S2_POOL_POS)) return;
          if (!Object.prototype.hasOwnProperty.call(have, h)) { s2Missing = true; return; }
          if (!canAdd(h)) return;
          var p = prod(h);
          if (!p) return;
          if (pinned(info.seed.handle, h)) return;   // R2: a pinned item is never the closer
          var natc = naturalVariant(h, info.seed.handle, info.seed.size, p);
          sellable(p).forEach(function (v) {
            if (natc && v.price > UPSIZE_MAX_RATIO * natc.price) return;   // R4
            if (v.price >= gapShelf && (info.tier === 1 || v.price <= cap)) {
              opts2.push({ over: v.price - gapShelf, key: info.key, h: h, v: v, p: p, nat: natc });
            }
          });
        });
        if (wantPump) {
          var pp = prod(PUMP);
          var want = litre >= 2 ? 'Twin Pack' : 'Single';
          sellable(pp).forEach(function (v) {
            if (v.title === want && v.price >= gapShelf) opts2.push({ over: v.price - gapShelf, key: [0, 0, 0], h: PUMP, v: v, p: pp });
          });
        }
        if (opts2.length) {
          opts2.sort(function (x, y) { return (x.over - y.over) || cmpKey(x.key, y.key) || cmpStr(x.h, y.h); });
          var best = opts2[0];
          if (best.h === PUMP) {
            chosen.push(pumpCard(best.p, best.v));
            prods[PUMP] = true; keys['role:accessory'] = true;
          } else {
            var alt = best.nat && norm(best.nat.title) !== norm(best.v.title) ? best.nat : null;
            push(best.h, 'gap', { d: best.v, a: alt, p: best.p });
          }
        }
      }

      /* S3 — the litre pump (Twin Pack for 2+ litre bottles) */
      if (wantPump && !prods[PUMP] && chosen.length < maxCards) {
        var pump = prod(PUMP);
        var wantT = litre >= 2 ? 'Twin Pack' : 'Single';
        var vs = sellable(pump).filter(function (v) { return v.title === wantT; });
        if (!vs.length) vs = sellable(pump);
        if (vs.length) {
          chosen.push(pumpCard(pump, vs[0]));
          prods[PUMP] = true; keys['role:accessory'] = true;
        }
      }

      /* S4 — one honest swap */
      var sw = findSwap(lines, swapPlan, prod, gm, dis);
      if (sw && chosen.length < maxCards) chosen.push(sw);

      /* fill — next routine completers in rank order */
      var fillMissing = false;
      for (i = 0; i < ranked.length; i++) {
        if (chosen.length >= maxCards) break;
        c = ranked[i];
        if (!canAdd(c)) continue;
        if (!Object.prototype.hasOwnProperty.call(have, c)) { fillMissing = true; break; }
        pv = pickVariants(c);
        if (pv) push(c, 'routine', pv);
      }

      var need = [];
      specials.forEach(function (h) { if (!Object.prototype.hasOwnProperty.call(have, h)) need.push(h); });
      if (s2Missing) {
        Object.keys(cands).filter(function (h) {
          var info = cands[h];
          return (info.tier === 1 || info.pos < S2_POOL_POS) && !Object.prototype.hasOwnProperty.call(have, h);
        }).sort(function (a, b) { return cmpKey(cands[a].key, cands[b].key) || cmpStr(a, b); })
          .forEach(function (h) { need.push(h); });
      }
      if (s1Missing || fillMissing || (chosen.length < maxCards && ranked.some(function (h) { return !Object.prototype.hasOwnProperty.call(have, h); }))) {
        ranked.forEach(function (h) { if (!Object.prototype.hasOwnProperty.call(have, h)) need.push(h); });
      }
      return { cards: chosen.slice(0, maxCards), need: need };
    }

    function loop() {
      var res = evaluate();
      if (!res.need.length || waves >= MAX_WAVES) return res.cards;
      return fetchWave(res.need, have).then(function (fresh) {
        if (fresh > 0) waves++;
        var progressed = res.need.some(function (h) { return Object.prototype.hasOwnProperty.call(have, h); });
        if (!progressed) return evaluate().cards;
        return loop();
      });
    }

    /* Wave 1: specials (pump, swap bundles) + the top candidates by key. */
    return fetchWave(specials.concat(ranked), have).then(function (fresh) {
      if (fresh > 0) waves++;
      return loop();
    });
  }

  /* Which bundles could be a swap for this cart (structure only; money later). */
  function planSwaps(lines) {
    var singles = lines.filter(function (l) { return !isBundle(l.handle); });
    var pairs = [], twins = [], handles = [];
    for (var i = 0; i < singles.length; i++) {
      for (var j = i + 1; j < singles.length; j++) {
        var a = singles[i], b = singles[j];
        var key = [a.handle, b.handle].sort().join('+');
        var duo = graph.swap && graph.swap[key];
        if (!duo || norm(a.variant) !== norm(b.variant) || a.qty !== b.qty) continue;
        pairs.push({ a: a, b: b, duo: duo });
        if (handles.indexOf(duo) < 0) handles.push(duo);
      }
    }
    singles.forEach(function (l) {
      var k = l.handle + '|' + l.variant + '|2';
      var twin = graph.stockup && graph.stockup[k];
      if (twin && l.qty >= 2) {
        twins.push({ l: l, twin: twin });
        if (handles.indexOf(twin) < 0) handles.push(twin);
      }
    });
    return { pairs: pairs, twins: twins, handles: handles };
  }

  /* find_swap(): duo (two singles, same size + qty) or FS 2 × 1 L → Twin Pack.
     Saving from live prices; ≥ 50p; never trades away free delivery. */
  function findSwap(lines, plan, prod, gm, dis) {
    var best = null;
    plan.pairs.forEach(function (pr) {
      if (dis && dis.handles[pr.duo]) return;   // prod() already drops dismissed product ids
      var dp = prod(pr.duo);
      if (!dp) return;
      var dv = findTitle(sellable(dp), pr.a.variant);
      if (!dv) return;
      var n = pr.a.qty;
      var saving = n * (pr.a.price + pr.b.price - dv.price);
      if (saving >= MIN_SWAP_SAVING && (!best || saving > best.saving)) {
        best = { saving: saving, p: dp, v: dv, n: n, lines: [pr.a, pr.b], type: 'duo' };
      }
    });
    plan.twins.forEach(function (tw) {
      if (dis && dis.handles[tw.twin]) return;
      var tp = prod(tw.twin);
      if (!tp) return;
      var tv = sellable(tp)[0];
      if (!tv) return;
      var n = Math.floor(tw.l.qty / 2);
      var saving = n * (2 * tw.l.price - tv.price);
      if (saving >= MIN_SWAP_SAVING && (!best || saving > best.saving)) {
        best = { saving: saving, p: tp, v: tv, n: n, lines: [tw.l], type: 'twin' };
      }
    });
    if (!best) return null;
    var newTotal = gm.total - Math.round(best.saving * (1 - gm.rate));
    if (!gm.freeByCode && gm.total >= gm.T && newTotal < gm.T) return null;   // TEST C20

    var updates = {};
    var replaces = [];
    if (best.type === 'duo') {
      best.lines.forEach(function (l) { l.keys.forEach(function (k) { updates[k] = 0; replaces.push(k); }); });
    } else {
      var l = best.lines[0];
      l.keys.forEach(function (k, idx) { updates[k] = idx === 0 ? l.qty - 2 * best.n : 0; replaces.push(k); });
    }
    updates[String(best.v.id)] = best.n;
    var size = sizeLabel(best.lines[0].variant);
    var note = best.type === 'duo'
      ? 'Replaces your ' + (best.n > 1 ? best.n + ' × ' : '') + (size ? size + ' ' : '') + 'shampoo and conditioner'
      : 'Replaces ' + (2 * best.n) + ' × ' + (size || 'bottles') + ' in your basket';
    var card = makeCard(best.p, best.p.handle, 'swap', best.v, null, null, { gap: 0 }, {
      reason: 'Same bottles, ' + money(best.saving) + ' less',
      chips: [], qty: best.n, saving: best.saving, replaces: replaces,
      replacesHandles: best.lines.map(function (x) { return x.handle; }),
      updates: updates, note: note, slot: 'swap'
    });
    return card;
  }

  /* Degraded cart (graph fetch failed): partner only + the pump rule. Never
     Shopify related/complementary/tools. */
  function degradedCart(cart, opts) {
    var lines = cartLines(cart);
    var owned = {};
    lines.forEach(function (l) { owned[l.handle] = true; });
    var gm = gapMaths(cart, opts);
    var dis = dismissedSets(opts.dismissed);
    var partners = [];
    lines.forEach(function (l) {
      var h = l.handle, ph = PARTNER[h];
      if (!ph && /-shampoo(-|$)/.test(h)) ph = h.replace('-shampoo', '-conditioner');
      else if (!ph && /-conditioner(-|$)/.test(h)) ph = h.replace('-conditioner', '-shampoo');
      if (ph && !owned[ph] && !dis.handles[ph] && !partners.some(function (x) { return x.h === ph; })) partners.push({ h: ph, size: l.variant });
    });
    var litre = lines.reduce(function (n, l) { return n + (isLitre(l.variant) ? l.qty : 0); }, 0);
    var wantPump = litre > 0 && !owned[PUMP];
    var order = partners.map(function (x) { return x.h; }).concat(wantPump ? [PUMP] : []);
    var have = {};
    return fetchWave(order, have).then(function () {
      var cards = [];
      partners.forEach(function (x) {
        var p = have[x.h];
        if (!p || dis.ids[String(p.id)]) return;
        var nat = sizeMatch(p, x.size);
        if (!nat) return;
        var d = nat, a = null;
        if (gm.gap) {
          var closer = null;
          sellable(p).forEach(function (v) { if (v.price >= gm.gapShelf && (!closer || v.price < closer.price)) closer = v; });
          if (closer && norm(closer.title) !== norm(nat.title)) {
            if (nat.price >= gm.gapShelf) a = closer;
            else if (closer.price <= UPSIZE_MAX_RATIO * nat.price) { d = closer; a = nat; }
            else a = closer;
          }
        }
        var card = makeCard(p, x.h, 'routine', d, a, null, gm);
        card.rel = 'partner';
        card.reason = /conditioner|cream-rinse|lotion/.test(x.h) ? 'Matching conditioner' : (/shampoo|wash/.test(x.h) ? 'Matching shampoo' : 'Completes your routine');
        cards.push(card);
      });
      if (wantPump && have[PUMP]) {
        var want = litre >= 2 ? 'Twin Pack' : 'Single';
        var vs = sellable(have[PUMP]).filter(function (v) { return v.title === want; });
        if (!vs.length) vs = sellable(have[PUMP]);
        if (vs.length) {
          var pc = makeCard(have[PUMP], PUMP, 'pump', vs[0], null, null, gm);
          pc.reason = 'Pump for your litre' + (litre >= 2 ? 's' : '');
          cards.splice(Math.min(1, cards.length), 0, pc);
        }
      }
      return cards.slice(0, MAX_CARDS);
    });
  }

  /* -------------------------------------------------------- forProduct ---- */
  function forProduct(product, variantTitle, cart, opts) {
    opts = opts || {};
    return ready().then(function (g) {
      if (!product || !product.handle) return [];
      return g ? pdpRecs(product, variantTitle, cart, opts) : degradedPdp(product, variantTitle, cart, opts);
    });
  }

  function selectedPrice(product, variantTitle) {
    var v = findTitle(product.variants || [], variantTitle) || (product.variants || [])[0];
    return v ? Number(v.price) || 0 : 0;
  }

  function pdpRecs(product, variantTitle, cart, opts) {
    var h = product.handle;
    var inCart = expandCart(cartLines(cart)).owned;
    var seeds, selfSet = {};
    selfSet[h] = true;
    if (isBundle(h)) {
      seeds = (bundleComponents(h, variantTitle || 'Default Title') || []).map(function (cp) {
        selfSet[cp[0]] = true;
        return { handle: cp[0], size: cp[1], qty: cp[2] };
      });
    } else {
      if (!node(h)) deriveRole(h, product.type);
      seeds = [{ handle: h, size: variantTitle || 'Default Title', qty: 1 }];
    }
    var cap = Math.max(PDP_PRICE_FLOOR_CAP, 2 * selectedPrice(product, variantTitle));
    var cands = {};
    seeds.forEach(function (s, si) {
      var ns = node(s.handle);
      if (!ns) return;
      function ok(c) {
        var nc = node(c);
        return !!(nc && !nc.derived && !selfSet[c] && !inCart[c] && c !== PUMP && !isBundle(c) && nc.s === ns.s &&
          !seeds.some(function (x) { return node(x.handle) && sameJob(x.handle, c); }));
      }
      var lst = (ns.x || []).slice();
      if (lst.filter(ok).length < 2) {
        fallbackList(s.handle).forEach(function (c) { if (lst.indexOf(c) < 0) lst.push(c); });
      }
      lst.forEach(function (c, pos) {
        if (!ok(c)) return;
        var tier = ns.pt === c ? 1 : (pos < (ns.x || []).length ? 2 : 3);
        var key = [tier, pos, si];
        if (!cands[c] || cmpKey(key, cands[c].key) < 0) cands[c] = { key: key, seed: s };
      });
    });
    var ranked = Object.keys(cands).sort(function (a, b) { return cmpKey(cands[a].key, cands[b].key) || cmpStr(a, b); });
    var litre = litreCount(seeds);
    var wantPump = litre > 0 && !inCart[PUMP];
    var specials = wantPump ? [PUMP] : [];
    var have = {};
    var waves = 0;

    function evaluate() {
      var out = [], keys = {}, prods = {}, missing = false;
      for (var i = 0; i < ranked.length; i++) {
        if (out.length >= MAX_CARDS) break;
        var c = ranked[i];
        if (prods[c] || keys[slotKey(c)]) continue;
        if (!Object.prototype.hasOwnProperty.call(have, c)) { missing = true; break; }
        var p = have[c];
        if (!p) continue;
        var info = cands[c];
        var v = naturalVariant(c, info.seed.handle, info.seed.size, p);
        if (!v) continue;
        var sameRangeTop = info.key[0] === 2 && info.key[1] < CAP_SAME_RANGE_POS && node(c).g === node(info.seed.handle).g;
        if (info.key[0] !== 1 && !sameRangeTop && v.price > cap) continue;
        out.push(makeCard(p, c, 'routine', v, null, info.seed.handle, null));
        prods[c] = true; keys[slotKey(c)] = true;
        if (out.length === 1 && wantPump) {
          var pp = have[PUMP];
          var want = litre >= 2 ? 'Twin Pack' : 'Single';
          var pv = pp ? findTitle(sellable(pp), want) : null;
          if (pv) {
            var pc = makeCard(pp, PUMP, 'pump', pv, null, null, null);
            pc.reason = 'Pump for your litre' + (litre >= 2 ? 's' : '');
            out.push(pc);
            prods[PUMP] = true; keys['role:accessory'] = true;
          }
        }
      }
      var need = specials.filter(function (x) { return !Object.prototype.hasOwnProperty.call(have, x); });
      if (missing) ranked.forEach(function (x) { if (!Object.prototype.hasOwnProperty.call(have, x)) need.push(x); });
      return { cards: out.slice(0, MAX_CARDS), need: need };
    }

    function loop() {
      var res = evaluate();
      if (!res.need.length || waves >= MAX_WAVES) return res.cards;
      return fetchWave(res.need, have).then(function (fresh) {
        if (fresh > 0) waves++;
        var progressed = res.need.some(function (x) { return Object.prototype.hasOwnProperty.call(have, x); });
        if (!progressed) return evaluate().cards;
        return loop();
      });
    }

    return fetchWave(specials.concat(ranked), have).then(function (fresh) {
      if (fresh > 0) waves++;
      return loop();
    });
  }

  /* Degraded PDP: the SSR list minus in-bag items; drop multi-variant items
     whose variants are not sizes (a shade/brush size could be wrong); pump rule. */
  function degradedPdp(product, variantTitle, cart, opts) {
    var inCart = {};
    ((cart && cart.items) || []).forEach(function (it) { inCart[it.handle] = true; });
    var ssr = (opts && opts.ssr) || [];
    var washRe = /(shampoo|conditioner|cream-rinse|co-?wash)/;
    var cards = [];
    ssr.forEach(function (p) {
      if (cards.length >= MAX_CARDS || !p || !p.handle || p.handle === product.handle || inCart[p.handle]) return;
      var vs = p.variants || [];
      if (vs.length > 1 && !vs.every(function (v) { return !!parseSize(v.title); })) return;
      var v = (washRe.test(p.handle) && washRe.test(product.handle)) ? sizeMatch(p, variantTitle) : defaultVariant(p);
      if (!v) return;
      cards.push(makeCard(p, p.handle, 'routine', v, null, null, null));
    });
    cards.forEach(function (c) { c.reason = 'Completes your routine'; });
    var wantPump = isLitre(variantTitle) && !inCart[PUMP];
    if (!wantPump || !cards.length) return Promise.resolve(cards);
    return getProduct(PUMP).then(function (pp) {
      var pv = pp ? findTitle(sellable(pp), 'Single') : null;
      if (pv) {
        var pc = makeCard(pp, PUMP, 'pump', pv, null, null, null);
        pc.reason = 'Pump for your litre';
        cards.splice(1, 0, pc);
      }
      return cards.slice(0, MAX_CARDS);
    });
  }

  /* ---------------------------------------------------------- dismissals -- */
  function dismissed() {
    try {
      var raw = W.sessionStorage.getItem(DISMISS_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.map(String) : [];
    } catch (e) { return memDismissed.slice(); }
  }
  var memDismissed = [];
  /* Stores the product id (the old key, so existing dismissals survive) and
     "h:<handle>" so the engine can drop it before any fetch. */
  function dismiss(productId, handle) {
    var add = [];
    if (productId != null && productId !== '') add.push(String(productId));
    if (handle) add.push('h:' + handle);
    var list = dismissed();
    add.forEach(function (k) { if (list.indexOf(k) < 0) list.push(k); if (memDismissed.indexOf(k) < 0) memDismissed.push(k); });
    try { W.sessionStorage.setItem(DISMISS_KEY, JSON.stringify(list)); } catch (e) { /* this render still honours it */ }
    return list;
  }

  /* Cart identity for the lastUpsellSig guard: lines, money and applied codes
     (a shipping-only code changes no money field but switches gap mode). */
  function sig(cart) {
    if (!cart) return '';
    return ((cart.items || []).map(function (i) { return i.key + ':' + i.quantity; }).join(',')) + '|' +
      cart.total_price + '|' + (cart.total_discount || 0) + '|' + appliedCodes(cart).join('+');
  }

  /* -------------------------------------------------------------- cart ---- */
  var lastCart = null;
  document.addEventListener('cart:updated', function (e) {
    lastCart = (e && e.detail && e.detail.cart) || null;   // a bare event means "stale, refetch"
  });
  /* The latest cart: from cart:updated (cart.js dispatches on its first render),
     else wait up to waitMs for that first render, else GET /cart.js once. */
  function getCart(waitMs) {
    if (lastCart) return Promise.resolve(lastCart);
    return new Promise(function (resolve) {
      var done = false;
      function finish(c) {
        if (done) return;
        done = true;
        document.removeEventListener('cart:updated', onEv);
        resolve(c || null);
      }
      function onEv(e) { if (e && e.detail && e.detail.cart) finish(e.detail.cart); }
      document.addEventListener('cart:updated', onEv);
      setTimeout(function () {
        if (done) return;
        fetch(rootUrl() + 'cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (c) { if (c && !lastCart) lastCart = c; finish(lastCart || c); })
          .catch(function () { finish(null); });
      }, waitMs || 0);
    });
  }

  /* ---------------------------------------------------------- analytics --- */
  function canTrack() {
    try {
      return typeof W.gtag === 'function' && !!(TheColor.utils && TheColor.utils.hasConsent && TheColor.utils.hasConsent());
    } catch (e) { return false; }
  }

  function gaItem(card, index, list, opt) {
    var o = opt || card;
    return {
      item_id: 'shopify_ZZ_' + card.productId + '_' + o.variantId,
      item_name: card.title,
      item_brand: 'Paul Mitchell',
      item_category: card.role || undefined,
      item_variant: sizeLabel(o.variantTitle) || undefined,
      price: (o.price || 0) / 100,
      index: index,
      item_list_id: list,
      item_list_name: list,
      rec_reason: card.rel,
      rec_free_delivery: o.crossesGap ? 'yes' : 'no'
    };
  }

  var track = {
    viewList: function (list, cards) {
      if (!canTrack() || !cards || !cards.length) return false;
      W.gtag('event', 'view_item_list', {
        item_list_id: list, item_list_name: list, rec_graph: version(),
        items: cards.map(function (c, i) { return gaItem(c, i, list); })
      });
      return true;
    },
    selectItem: function (list, card, index, opt) {
      if (!canTrack() || !card) return;
      W.gtag('event', 'select_item', { item_list_id: list, item_list_name: list, items: [gaItem(card, index, list, opt)] });
    },
    dismiss: function (list, card) {
      if (!canTrack() || !card) return;
      W.gtag('event', 'upsell_dismiss', { item_list_name: list, item_id: 'shopify_ZZ_' + card.productId + '_' + card.variantId });
    }
  };

  /* List context for TheColor.Cart.addToCart(…, listContext) — cart.js puts
     these on the add_to_cart items. */
  function listContext(list, card, index, opt) {
    var o = opt || card;
    return {
      list: list, index: index, reason: card.rel,
      freeDelivery: o.crossesGap ? 'yes' : 'no',
      saving: card.kind === 'swap' ? card.saving : undefined
    };
  }

  function version() { return (graph && graph.v) || ''; }

  /* ------------------------------------------------------------- export --- */
  TheColor.Recs = {
    ready: ready,
    forCart: forCart,
    forProduct: forProduct,
    getProduct: getProduct,
    prime: prime,
    dismiss: dismiss,
    dismissed: dismissed,
    sig: sig,
    version: version,
    getCart: getCart,
    listContext: listContext,
    track: track,
    money: money,
    escapeHtml: escapeHtml,
    escapeAttr: escapeAttr,
    parseSize: parseSize,
    /* test hooks (QC): the pure pieces, for reproducing a row in the console */
    _internals: { cartLines: cartLines, expandCart: expandCart, gapMaths: gapMaths, naturalVariant: naturalVariant,
      fallbackList: fallbackList, reason: reason, slotKey: slotKey, graph: function () { return graph; } }
  };
  try { document.dispatchEvent(new CustomEvent('recs:ready')); } catch (e) { /* very old browser */ }
})();
